import { ReactNode, useState } from "react";
import {
  Card,
  CloseButton,
  Col,
  Container,
  Row,
  Table,
  Badge,
  Button,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

//spotsync controls
import { ContactModal } from "./FacilityParkerDetailsPageContactModal";
import { cents, Money, showMoney, toCents } from "./Data/Money";
import { add } from "dinero.js";
import { useParkerBalance } from "./hooks/useParkerBalance";
import { useLoaderData } from "react-router-dom";
import { ParkerDetails, ParkerUpdateReq, Vehicle } from "./Data/ApiTypes";
import { showDateTime } from "./Data/Date";
import { Cond, useParamIds } from "./Data/Common";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import {
  Pencil,
  Envelope,
  Plus,
  ExclamationCircleFill,
} from "react-bootstrap-icons";
import { ClipboardLink } from "./Controls/ClipboardLink";
import { getStateEnumFromCode, showStateName } from "./Data/UsState";
import { PageBar, ToolButton } from "./Controls/PageBar";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { AddCreditFormModal } from "./FacilityParkerPaymentListPageAddCreditModal";
import { showSubscriptionStatus } from "./Data/Subscription";
import { useHandleNavigation } from "./shared/navigation";
import { FacilityInvoiceCreatePage } from "./FacilityCustomOrderCreatePage";
import { useAuthorization } from "./hooks/useAuthorization";
import './FacilityParkerDetailsPage.css'

interface BigStatProps {
  label: ReactNode;
  value: Money;
  good?: boolean;
  warn?: boolean;
  bad?: boolean;
}

const BigStat = ({ label, value, good, warn, bad }: BigStatProps) => {
  const goodCol = good ? "text-success" : "";
  const warnCol = warn ? "text-warning" : "";
  const erroCol = bad ? "text-danger" : "";
  return (
    <Cond show={true}>
      <div style={{ flex: "1 1 0" }}>
        <div className={`text-secondary text-center`}>{label}</div>
        <div className={`fs-4 text-center ${goodCol} ${warnCol} ${erroCol}`}>
          {showMoney(value)}
        </div>
      </div>
    </Cond>
  );
};

interface ReservationVehicleCardProps {
  vehicle: Vehicle;
  onRemove: (v: Vehicle) => void;
}

function ReservationVehicleCard({
  vehicle,
  onRemove,
}: ReservationVehicleCardProps) {
  return (
    <div className="mb-2 m-2">
      <div
        className="gap-1"
        style={{
          display: "grid",
          alignItems: "center" /* color make model */,
          gridTemplateColumns: "min-content 1fr auto",
        }}
      >
        <div
          className="rounded-2 border border-secondary me-2"
          style={{
            height: "32px",
            width: "32px",
            backgroundColor: vehicle.color,
          }}
        />
        <div
          style={{
            display: "grid",
            alignItems: "center",
            gridTemplateRows: "auto auto",
          }}
        >
          <div className="fw-bold d-flex flex-column gap-1">
            {vehicle.make} {vehicle.model}
          </div>
          <div className="grid-col-1">
            {showStateName(getStateEnumFromCode(vehicle.stateCode)!)}{" "}
            {vehicle.licensePlateNumber}
          </div>
        </div>
        <div className="ms-3">
          <CloseButton onClick={() => onRemove(vehicle)} />
        </div>
      </div>
    </div>
  );
}

export function FacilityParkerDetailsPage() {
  const { facilityId, parkerId } = useParamIds();
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [showCustomOrder, setShowCustomOrder] = useState(false);
  const [showAddCredit,   setShowAddCredit  ] = useState(false);
  const loadedParker = useLoaderData() as ParkerDetails;
  const [parker, setParker] = useState(loadedParker);
  const handleNavigation = useHandleNavigation();
  const nav = useNavigate();
  const { applyCredit, applyOrderAdjust } = useParkerBalance();
  const { canPerformAction } = useAuthorization();
  const canAddCredit = canPerformAction('add-credit');
  const canManageMonthly = canPerformAction('manage-monthly');

  const reservations = parker.reservations;

  const activeSubs = parker.subscriptions.filter(
    (s) => s.status === SubscriptionStatus.Active
  );
  const pendingSubs = parker.subscriptions.filter(
    (s) =>
      s.status === SubscriptionStatus.WaitingForContract ||
      s.status === SubscriptionStatus.Applied
  );
  const inactiveSubs = parker.subscriptions.filter(
    (s) =>
      s.status === SubscriptionStatus.Cancelled ||
      s.status === SubscriptionStatus.Rejected || 
      s.status === SubscriptionStatus.UnpaidAndRevoked
  );

  const resTotal = reservations
    .map((r) => r.price)
    .reduce((a, b) => add(a, b), cents(0));
  const monthlySpend = activeSubs
    .map((s) => (s.price ? s.price : cents(0)))
    .reduce((a, b) => add(a, b), cents(0));

  function handleCreditApplied(val: { amount: Money }): void {
    applyCredit(val);                                          // updates sidebar badge
    setParker({ ...parker, balance: cents(toCents(parker.balance) - toCents(val.amount)) });
  }

  function saveContact(data: ParkerUpdateReq): void {
    Api.parkerUpdate(parkerId, data).then((resp) =>
      processResult(
        resp,
        (val) => setParker({ ...parker, ...data }),
        (err) => undefined
      )
    );
  }

  return (
    <Container fluid>
      <ContactModal
        parker={parker}
        show={showContactInfo && canManageMonthly}
        setShow={setShowContactInfo}
        onSave={saveContact}
      />

      {canAddCredit && (
      <AddCreditFormModal
        show={showAddCredit}
        setShow={setShowAddCredit}
        facilityId={facilityId}
        parkerId={parkerId}
        onSave={data => {
          Api.paymentCreditCreate(facilityId, parkerId, data).then(res =>
            processResult(res, handleCreditApplied),
            err => console.log(err)
          );
        }}
      />
      )}

      <Row>
        <Col>
          {!showCustomOrder && (
            <>
              <div className="d-none d-md-block">
                <div className="d-flex align-items-center gap-2 mb-0">
                  <PageBar title={parker.name}>
                    {canManageMonthly && (
                    <ToolButton onClick={() => setShowContactInfo(true)}>
                      <Pencil /> Edit
                    </ToolButton>
                    )}
                    {canManageMonthly && (
                    <ToolButton
                      onClick={() =>
                        nav(`/facility/${facilityId}/parker/invite`, {
                          state: {
                            email: parker.emailAddress,
                            name: parker.name,
                          },
                        })
                      }
                    >
                      <Envelope /> Invite
                    </ToolButton>
                    )}
                    <ToolButton onClick={() => setShowCustomOrder(true)}>
                      <Plus color="maroon" /> Order
                    </ToolButton>
                    {canAddCredit && (
                    <ToolButton onClick={() => setShowAddCredit(true)}>
                      <Plus color="magenta" /> Credit
                    </ToolButton>
                    )}
                  </PageBar>
                </div>
              </div>

              <div className="d-block d-md-none">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <Button
                    variant="link"
                    className="p-0"
                    onClick={() => nav(-1)}
                  >
                    ← Back
                  </Button>
                  <span className="fw-bold">{parker.name}</span>
                </div>

                <div className="d-flex flex-wrap gap-2 mb-3">
                  {canManageMonthly && (
                  <ToolButton
                    onClick={() => setShowContactInfo(true)}
                    className="tool-button-xs"
                  >
                    <Pencil /> Edit
                  </ToolButton>
                  )}

                  {canManageMonthly && (
                  <ToolButton
                    onClick={() =>
                      nav(`/facility/${facilityId}/parker/invite`, {
                        state: {
                          email: parker.emailAddress,
                          name: parker.name,
                        },
                      })
                    }
                    className="tool-button-xs"
                  >
                    <Envelope /> Invite
                  </ToolButton>
                  )}

                  <ToolButton
                    onClick={() => setShowCustomOrder(true)}
                    className="tool-button-xs"
                  >
                    <Plus color="maroon" /> Order
                  </ToolButton>

                  {canAddCredit && (
                  <ToolButton
                    onClick={() => setShowAddCredit(true)}
                    className="tool-button-xs"
                  >
                    <Plus color="magenta" /> Credit
                  </ToolButton>
                  )}
                </div>
              </div>
            </>
          )}

          {showCustomOrder ? (
            <FacilityInvoiceCreatePage
              recipientOverride={{
                name: parker.name,
                emailAddress: parker.emailAddress,
                phoneNumber: parker.phoneNumber,
              }}
              setShowCustomOrder={setShowCustomOrder}
              onOrderCreated={order => applyOrderAdjust( order.balance.balance )}
            />
          ) : (
            <>
              <div
                className="d-grid gap-1 w-100"
                style={{
                  gridTemplateColumns: "auto 1fr",
                  wordBreak: "break-word",
                }}
              >
                {!!parker.phoneNumber && (
                  <>
                    <span>Phone</span>
                    <ClipboardLink>{parker.phoneNumber}</ClipboardLink>
                  </>
                )}
                {!!parker.emailAddress && (
                  <>
                    <span>Email</span>
                    <ClipboardLink>{parker.emailAddress}</ClipboardLink>
                  </>
                )}
              </div>

              <div className="row text-center my-4">
                <div className="col-4">
                  <BigStat
                    good={
                      toCents(parker.balance) <= 0 &&
                      !(loadedParker.nonRenewedSubscriptionIds?.length > 0)
                    }
                    label="Balance"
                    value={parker.balance}
                  />
                </div>
                <div className="col-4">
                  <BigStat label="Monthly" value={monthlySpend} />
                </div>
                <div className="col-4">
                  <BigStat label="Reservations" value={resTotal} />
                </div>
              </div>
            </>
          )}
        </Col>

        {!showCustomOrder && (
          <Col md="12" className="g-0 px-0">
            <Card className="mb-4">
              <Card.Body>
                <Row className="mb-4">
                  <Col sm={12} className="h4">
                    Active Plans
                  </Col>
                  <Col sm={12}>
                    {activeSubs.length === 0 ? (
                      <p>This parker does not have any active parking plans</p>
                    ) : (
                      <>
                        <div className="d-block d-md-none">
                          <div className="row g-2">
                            {activeSubs.map((s) => (
                              <div className="col-12" key={s.subscriptionId}>
                                <Card
                                  className="shadow-sm rounded-3"
                                  onClick={() =>
                                    handleNavigation(
                                      `/facility/${facilityId}/parker/${parkerId}/plan/${s.subscriptionId}`
                                    )
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <span className="fw-bold">
                                        {s.plan.name}
                                      </span>
                                      {s.hasAutoBillingEnabled && (
                                        <Badge className="ms-2 me-2">
                                          Auto
                                        </Badge>
                                      )}
                                      {loadedParker.nonRenewedSubscriptionIds?.includes(
                                        s.subscriptionId
                                      ) && (
                                        <Badge bg="black" className="opacity-75">
                                          <ExclamationCircleFill className="me-1 icon-shift-up" />
                                          Failed to renew
                                        </Badge>
                                      )}
                                    </div>

                                    <div className="d-flex justify-content-between small text-muted">
                                      <span>
                                        Quantity:{" "}
                                        <span className="fw-bold">
                                          {s.quantity}
                                        </span>
                                      </span>
                                      <span>
                                        Parkers:{" "}  
                                        <span className="fw-bold">
                                          {s.guests.length}
                                        </span>
                                      </span>
                                    </div>

                                    <div className="d-flex justify-content-between small text-muted mt-2">
                                      <span>
                                        Unit Price:{" "}
                                        {s.unitPrice && (
                                          <span className="fw-bold">
                                            {showMoney(s.unitPrice)}
                                          </span>
                                        )}
                                      </span>
                                      <span>
                                        Monthly Price:{" "}
                                        {s.price && (
                                          <span className="fw-bold">
                                            {showMoney(s.price)}
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="d-none d-md-block">
                          <Table hover style={{ tableLayout: "fixed" }}>
                            <thead>
                              <tr>
                                <th style={{ width: "30%" }}>Name</th>
                                <th className="text-end">Quantity</th>
                                <th className="text-end">Parkers</th>
                                <th className="text-center">Unit Price</th>
                                <th className="text-center">Monthly Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {activeSubs.map((s) => (
                                <tr key={s.subscriptionId}>
                                  <td>
                                    {s.hasAutoBillingEnabled && (
                                      <Badge className="ms-2 me-2">Auto</Badge>
                                    )}
                                    {loadedParker.nonRenewedSubscriptionIds?.includes(
                                      s.subscriptionId
                                    ) && (
                                      <Badge bg="black" className="ms-1 me-2 opacity-75">
                                        <ExclamationCircleFill className="me-1 icon-shift-up" />
                                        Failed to renew
                                      </Badge>
                                    )}
                                    <Link
                                      to="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleNavigation(
                                          `/facility/${facilityId}/parker/${parkerId}/plan/${s.subscriptionId}`
                                        );
                                      }}
                                    >
                                      {s.plan.name}
                                    </Link>
                                  </td>
                                  <td className="text-end">{s.quantity}</td>
                                  <td className="text-end">
                                    {s.guests.length}
                                  </td>
                                  <td className="text-center">
                                    {s.unitPrice && showMoney(s.unitPrice)}
                                  </td>
                                  <td className="text-center">
                                    {s.price && showMoney(s.price)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    )}
                  </Col>
                </Row>

                {pendingSubs.length > 0 && (
                  <Row className="mb-4">
                    <Col sm={12} className="h4">
                      Pending Plans
                    </Col>
                    <Col sm={12}>
                      <div className="d-none d-md-block">
                        <Table hover style={{ tableLayout: "fixed" }}>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th className="text-start">Status</th>
                              <th className="text-end">Quantity</th>
                              <th className="text-end">Parkers</th>
                              <th className="text-center">Unit Price</th>
                              <th className="text-center">Monthly Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pendingSubs.map((s) => (
                              <tr key={s.subscriptionId}>
                                <td>
                                  <Link
                                    to="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleNavigation(
                                        `/facility/${facilityId}/parker/${parkerId}/plan/${s.subscriptionId}`
                                      );
                                    }}
                                  >
                                    {s.plan.name}
                                  </Link>
                                </td>
                                <td className="text-start">
                                  {showSubscriptionStatus(s.status)}
                                </td>
                                <td className="text-end">{s.quantity}</td>
                                <td className="text-end">{s.guests.length}</td>
                                <td className="text-center">
                                  {s.unitPrice && showMoney(s.unitPrice)}
                                </td>
                                <td className="text-center">
                                  {s.price && showMoney(s.price)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      <div className="d-md-none">
                        <div className="row g-2">
                          {pendingSubs.map((s) => (
                            <div className="col-12" key={s.subscriptionId}>
                              <Card
                                className="shadow-sm rounded-3"
                                onClick={() =>
                                  handleNavigation(
                                    `/facility/${facilityId}/parker/${parkerId}/plan/${s.subscriptionId}`
                                  )
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <Card.Body>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="fw-bold">
                                      {s.plan.name}
                                    </span>
                                  </div>
                                  <div className="small text-muted mb-2">
                                    Status:{" "}
                                    <span className="fw-bold">
                                      {showSubscriptionStatus(s.status)}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between small text-muted">
                                    <span>
                                      Quantity:{" "}
                                      <span className="fw-bold">
                                        {s.quantity}
                                      </span>
                                    </span>
                                    <span>
                                      Parkers:{" "}
                                      <span className="fw-bold">
                                        {s.guests.length}
                                      </span>
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between small text-muted mt-2">
                                    <span>
                                      Unit Price:{" "}
                                      {s.unitPrice && (
                                        <span className="fw-bold">
                                          {showMoney(s.unitPrice)}
                                        </span>
                                      )}
                                    </span>
                                    <span>
                                      Monthly Price:{" "}
                                      {s.price && (
                                        <span className="fw-bold">
                                          {showMoney(s.price)}
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                </Card.Body>
                              </Card>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                {inactiveSubs.length > 0 && (
                  <Row className="mb-4">
                    <Col sm={12} className="h4">
                      Inactive Plans
                    </Col>
                    <Col sm={12}>
                      <div className="d-none d-md-block">
                        <Table hover style={{ tableLayout: "fixed" }}>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th className="text-start">Status</th>
                              <th className="text-end">Quantity</th>
                              <th className="text-end">Parkers</th>
                              <th className="text-center">Unit Price</th>
                              <th className="text-center">Monthly Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inactiveSubs.map((s) => (
                              <tr key={s.subscriptionId}>
                                <td>
                                  <Link
                                    to="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleNavigation(
                                        `/facility/${facilityId}/parker/${parkerId}/plan/${s.subscriptionId}`
                                      );
                                    }}
                                  >
                                    {s.plan.name}
                                  </Link>
                                </td>
                                <td className="text-start">
                                  {showSubscriptionStatus(s.status)}
                                </td>
                                <td className="text-end">{s.quantity}</td>
                                <td className="text-end">{s.guests.length}</td>
                                <td className="text-center">
                                  {s.unitPrice && showMoney(s.unitPrice)}
                                </td>
                                <td className="text-center">
                                  {s.price && showMoney(s.price)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      <div className="d-md-none">
                        <div className="row g-2">
                          {inactiveSubs.map((s) => (
                            <div className="col-12" key={s.subscriptionId}>
                              <Card
                                className="shadow-sm rounded-3"
                                onClick={() =>
                                  handleNavigation(
                                    `/facility/${facilityId}/parker/${parkerId}/plan/${s.subscriptionId}`
                                  )
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <Card.Body>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="fw-bold">
                                      {s.plan.name}
                                    </span>
                                  </div>
                                  <div className="small mb-2 small text-muted">
                                    Status:{" "}
                                    <span className="fw-bold">
                                      {showSubscriptionStatus(s.status)}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between small text-muted">
                                    <span>
                                      Quantity:{" "}
                                      <span className="fw-bold">
                                        {s.quantity}
                                      </span>
                                    </span>
                                    <span>
                                      Parkers:{" "}
                                      <span className="fw-bold">
                                        {s.guests.length}
                                      </span>
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between small text-muted mt-2">
                                    <span>
                                      Unit Price:{" "}
                                      {s.unitPrice && (
                                        <span className="fw-bold">
                                          {showMoney(s.unitPrice)}
                                        </span>
                                      )}
                                    </span>
                                    <span>
                                      Monthly Price:{" "}
                                      {s.price && (
                                        <span className="fw-bold">
                                          {showMoney(s.price)}
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                </Card.Body>
                              </Card>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                <Row className="mb-4">
                  <Col sm={12} className="h4">
                    Reservations
                  </Col>
                  <Col sm={12}>
                    {reservations.length === 0 ? (
                      <p>This parker has not made any reservations</p>
                    ) : (
                      <>
                        <div className="d-none d-md-block">
                          <Table hover striped>
                            <thead>
                              <tr>
                                <th>Rate</th>
                                <th>Check-In</th>
                                <th>Check-Out</th>
                                <th>Vehicle</th>
                                <th>Price</th>
                                <th>Spaces</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reservations.map((r) => (
                                <tr key={r.reservationId}>
                                  <td>{r.rateProgram.name}</td>
                                  <td>{showDateTime(r.start)}</td>
                                  <td>{showDateTime(r.end)}</td>
                                  <td>Not Specified</td>
                                  <td>
                                    {showMoney(r.price)}
                                  </td>
                                  <td>{r.spaces}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        <div className="d-block d-md-none">
                          <div className="row g-2">
                            {reservations.map((r) => (
                              <div className="col-12" key={r.reservationId}>
                                <Card className="shadow-sm rounded-3">
                                  <Card.Body>
                                    <div className="fw-bold mb-2">
                                      {r.rateProgram.name}
                                    </div>

                                    <div className="small text-muted">
                                      Check-In:{" "}
                                      <span className="fw-bold">
                                        {showDateTime(r.start)}
                                      </span>
                                    </div>

                                    <div className="small text-muted  mt-2">
                                      Check-Out:{" "}
                                      <span className="fw-bold">
                                        {showDateTime(r.end)}
                                      </span>
                                    </div>

                                    <div className="small text-muted  mt-2">
                                      Vehicle:{" "}
                                      <span className="fw-bold">
                                        Not Specified
                                      </span>
                                    </div>

                                    <div className=" justify-content-between small text-muted mt-2">
                                      Price:{" "}
                                      <span className="fw-bold">
                                        {showMoney(r.price)}
                                      </span>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col sm={12} className="h4">
                    Vehicles
                  </Col>
                  <Col sm={12}>
                    {parker.vehicles.length === 0 ? (
                      <p>This parker has not added any vehicles</p>
                    ) : (
                      <Table hover>
                        <thead>
                          <tr>
                            <th>Make/Model</th>
                            <th>Color</th>
                            <th>License Plate</th>
                            <th>State</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parker.vehicles.map((vehicle) => (
                            <tr key={vehicle.vehicleId}>
                              <td>
                                {vehicle.make} {vehicle.model}
                              </td>
                              <td>{vehicle.color}</td>
                              <td>{vehicle.licensePlateNumber}</td>
                              <td>{vehicle.stateCode}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </Container>
  );
}
